import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { type ReactNode, useCallback, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import { FilterChipButton } from "@packages/core-client/collection/FilterChip.tsx";

export interface FilterChipPopoverProps<TKind extends string> {
    kind: TKind;
    label: string;
    value?: string;
    ariaLabel?: string;
    tone?: "neutral" | "active";
    removeLabel: string;
    onRemove: (kind: TKind) => void;
    children: (close: () => void) => ReactNode;
}

export function FilterChipPopover<TKind extends string>({ kind, label, value, ariaLabel, tone = "neutral", removeLabel, onRemove, children }: FilterChipPopoverProps<TKind>) {
    const [isOpen, setIsOpen] = useState(false);
    const close = useCallback(() => setIsOpen(false), []);
    const handleRemove = useCallback(() => onRemove(kind), [onRemove, kind]);

    return (
        <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
            <FilterChipButton isActive={tone === "active"} aria-label={ariaLabel} className={value === undefined ? "max-w-64" : undefined}>
                {value === undefined ? (
                    <span className="min-w-0 truncate">{label}</span>
                ) : (
                    <>
                        <span className="text-muted-fg">{label}</span>
                        <span className="max-w-40 truncate font-semibold">{value}</span>
                    </>
                )}
                <ChevronDownIcon className="text-muted-fg size-4" aria-hidden="true" />
            </FilterChipButton>
            <PopoverContent placement="bottom start" aria-label={label} className="bg-bg p-3 [&_[data-slot=popover-inner]]:overflow-x-hidden">
                {children(close)}
                <div className="mt-2 flex justify-end">
                    <Button intent="plain" size="sm" onPress={handleRemove}>
                        {removeLabel}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
